import {
	DEFAULT_HUB_TEXT_SIZE,
	getFontScaleFromAppThemeClaim,
	type HubTextSize,
} from './appTheme';
import { applyHubTextSize } from './applyAppTheme';
import { loadHubTextSize, writeHubTextSize } from './appThemeStorage';
import { getKeycloak } from './keycloak';
import { updateAccountAppTheme } from './keycloakAccountAppTheme';
import { decodeJwtPayload } from './keycloakTokenStorage';
import { ensureFreshKeycloakToken } from './keycloakTokenRefresh';

type JwtAppThemePayload = {
	app_theme?: unknown;
};

/**
 * Applies the default text size immediately (no Preferences read — sync path for first paint).
 * Prefer {@link applyCachedAppTheme} / {@link syncAppThemeFromAccessToken} once auth is ready.
 * Do not call this from Settings UI remounts; it always resets to Default.
 */
export function applyCachedAppThemeSync(): HubTextSize {
	applyHubTextSize(DEFAULT_HUB_TEXT_SIZE);

	return DEFAULT_HUB_TEXT_SIZE;
}

/**
 * Loads cached size from Preferences and applies it.
 */
export async function applyCachedAppTheme(): Promise<HubTextSize> {
	const size = await loadHubTextSize();
	applyHubTextSize(size);

	return size;
}

/**
 * Prefer Keycloak's in-memory token over a caller-passed token (Pinia may be stale).
 */
function resolveAccessTokenForThemeSync(accessToken: string | undefined): string | undefined {
	const fromKeycloak = getKeycloak().token?.trim();
	if (fromKeycloak) {
		return fromKeycloak;
	}

	const fromCaller = accessToken?.trim();

	return fromCaller || undefined;
}

/**
 * Prefer `app_theme` from the access token when present; update cache + DOM.
 * Returns the resolved size.
 */
export async function syncAppThemeFromAccessToken(
	accessToken: string | undefined,
): Promise<HubTextSize> {
	const token = resolveAccessTokenForThemeSync(accessToken);
	if (!token) {
		return applyCachedAppTheme();
	}

	const payload = decodeJwtPayload(token) as JwtAppThemePayload | undefined;
	const fromToken = getFontScaleFromAppThemeClaim(payload?.app_theme);

	if (fromToken) {
		applyHubTextSize(fromToken);
		await writeHubTextSize(fromToken);

		return fromToken;
	}

	return applyCachedAppTheme();
}

export type SetHubTextSizeResult = {
	size: HubTextSize;
	synced: boolean;
	error?: string;
};

/**
 * Optimistic local apply + persist, then Keycloak Account API + forced token refresh.
 * Forced refresh goes through {@link ensureFreshKeycloakToken} so hub `onTokenUpdated` runs.
 */
export async function setHubTextSize(
	size: HubTextSize,
	accessToken?: string | null,
): Promise<SetHubTextSizeResult> {
	applyHubTextSize(size);
	await writeHubTextSize(size);

	const token = accessToken?.trim() || getKeycloak().token;
	if (!token) {
		return { size, synced: false, error: 'Not signed in — saved on this device only.' };
	}

	try {
		await updateAccountAppTheme(token, size);

		const keycloak = getKeycloak();
		if (keycloak.authenticated || keycloak.refreshToken) {
			// -1 forces refresh so the new app_theme claim is available and onTokenUpdated fires.
			await ensureFreshKeycloakToken(-1);
		}

		return { size, synced: true };
	} catch (error) {
		const message = error instanceof Error ? error.message : 'Failed to sync preference';

		return { size, synced: false, error: message };
	}
}

export function getDefaultHubTextSize(): HubTextSize {
	return DEFAULT_HUB_TEXT_SIZE;
}
